import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

interface SuitabilitySectionProps {
  onCTAClick: () => void;
  className?: string;
}

export default function SuitabilitySection({ onCTAClick }: SuitabilitySectionProps) {
  const suitable = [
    "المبتدئين اللي بدهم خطة واضحة.",
    "المشغولين اللي بدهم مصدر دخل إضافي.",
    "الشخص الجاد يلتزم 30 يوم ليتعلم مهارة تساعده في تحسين دخله.",
  ];

  const notSuitable = [
    "اشخاص بدهم أرباح مضمونة 100%.",
    "اشخاص بدهم ثراء سريع بدون التزام.",
    "اشخاص مش ناويين يلتزموا يوم واحد.",
  ];

  return (
    <section className="relative py-24 bg-[#000000] overflow-hidden">
      {/* Animated Border Glow */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#CFAE58] to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#0A1E3F] to-transparent"></div>
      </div>

      {/* Glowing Corners */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-[#CFAE58] rounded-full blur-[150px] opacity-10"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#0A1E3F] rounded-full blur-[150px] opacity-10"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            لمين التحدي هاد <span className="text-[#CFAE58]">مناسب \ مش مناسب</span> ؟
          </h2>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Suitable */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#CFAE58]/10 to-transparent backdrop-blur-sm border-2 border-[#CFAE58]/50 rounded-2xl p-8 h-full shadow-2xl shadow-[#CFAE58]/10">
                {/* Glowing Border Effect */}
                <div className="absolute -inset-[1px] bg-gradient-to-br from-[#CFAE58]/20 to-transparent rounded-2xl blur-sm -z-10"></div>

                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="bg-[#CFAE58]/20 p-4 rounded-full">
                    <Check className="w-10 h-10 text-[#CFAE58]" strokeWidth={3} />
                  </div>
                  <h3 className="text-3xl text-[#CFAE58]">مناسب</h3>
                </div>

                {/* Points */}
                <div className="space-y-5">
                  {suitable.map((point, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start gap-4 bg-[#CFAE58]/5 rounded-xl p-5 border border-[#CFAE58]/20 hover:border-[#CFAE58]/40 transition-all hover:bg-[#CFAE58]/10"
                    >
                      <Check className="w-6 h-6 text-[#CFAE58] mt-1 flex-shrink-0" strokeWidth={2.5} />
                      <p className="text-white text-lg leading-relaxed">{point}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Not Suitable */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#0A1E3F]/30 to-transparent backdrop-blur-sm border-2 border-[#0A1E3F]/50 rounded-2xl p-8 h-full">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="bg-red-500/20 p-4 rounded-full">
                    <X className="w-10 h-10 text-red-400" strokeWidth={3} />
                  </div>
                  <h3 className="text-3xl text-red-400">مش مناسب</h3>
                </div>

                {/* Points */}
                <div className="space-y-5">
                  {notSuitable.map((point, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start gap-4 bg-red-900/10 rounded-xl p-5 border border-red-500/20"
                    >
                      <X className="w-6 h-6 text-red-400 mt-1 flex-shrink-0" strokeWidth={2.5} />
                      <p className="text-gray-300 text-lg leading-relaxed">{point}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
   <button
  onClick={onCTAClick}
  className="bg-[#CFAE58] text-white px-12 py-5 rounded-xl hover:bg-[#B89A45] transition-all duration-300 
             shadow-[0_0_25px_rgba(207,174,88,0.9),_0_0_50px_rgba(207,174,88,0.6)] 
             transform hover:scale-105 text-xl drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]"
>
  سجّل بياناتك وابدأ فورًا
</button>

          </motion.div>
        </div>
      </div>
    </section>
  );
}
